import * as React from 'react';
import { connect } from 'react-redux';

import { PromptExit } from 'libs/router';

import ActionButton from 'components/buttons/action-button/ActionButton';
import BrandsDropdown from 'components/brands-filter/brands-dropdown/BrandsDropdown';
import CollectionImportedModalContent from 'components/modal/collection-imported-modal-content/CollectionImportedModalContent';
import SubcampaignsMultiSelect from 'modules/subcampaigns/components/bulk/subcampaigns-multi-select/SubcampaignsMultiSelect';
import CampaignsMultiSelect from 'modules/campaigns/components/campaigns-multi-select/CampaignsMultiSelect';
import { ModalFrame, ModalFrameFooter, ModalFrameInner } from 'components/modal';

import { danger } from 'redux-store/reducers/common-reducer';
import {
  CreateSubcampaignsBulkResponse,
  fetchApprovedInfluencers,
  fetchCampaigns,
  fetchInfluencers,
  performBulkAddToShortlists,
  reset
} from '../reducers/bulkAddToShortlists';
import { fetchInfluencersMeta } from 'modules/influencers/reducers/influencersMeta';

import { __t, applyTranslations } from 'utils/language';
import { getSelectedCampaigns, getSelectedSubcampaigns } from 'utils/bulk-offer';

import type {
  Campaign,
  List,
  Subcampaign,
  SubcampaignSelectedIdsMap
} from 'types';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./bulk-add-to-shortlist-modal.i18n.json'));

type OwnProps = {
  organizationId: number,
  showOnlyApproved: boolean,
  list: List,
  onCloseModal: () => void
};

type StateProps = {
  areSubcampaignsLoading: boolean,
  areCampaignsLoading: boolean,
  futureSubcampaigns: Array<Subcampaign>,
  filter: string,
  campaigns: Array<Campaign>,
  organizationsListIds: Array<number>
};

type DispatchProps = {
  dispatchFetchCampaigns: (filterBy: string) => Promise<void>,
  dispatchFetchListInfluencers: (orgId: number, listId: number) => Promise<void>,
  dispatchFetchApprovedListInfluencers: (orgId: number, listId: number) => Promise<void>,
  dispatchPerformBulkAddToShortlists: (campaignIds: Array<number>, influencerIds: Array<number>) => Promise<Array<CreateSubcampaignsBulkResponse>>,
  dispatchReset: () => Promise<void>,
  dispatchDanger: (message: string) => Promise<void>,
  dispatchUpdateMeta: (influencerIds: Array<number>) => Promise<void>
};

type Props = OwnProps & StateProps & DispatchProps;

type Step = 'select_influencers' | 'select_campaigns' | 'done';

type State = {
  isModified: boolean,
  selectedCreatorIdsMap: SubcampaignSelectedIdsMap,
  selectedCampaignIdsMap: {
    [x: number]: boolean
  },
  step: Step,
  filter: string,
  existingCreators: Array<string>
};

const STEPS: Array<Step> = ['select_influencers', 'select_campaigns', 'done'];
const STEP_MIN_HEIGHT = 352;

export class BulkAddToShortlistModal extends React.PureComponent<Props, State> {
  state: State = {
    isModified: false,
    selectedCreatorIdsMap: {},
    selectedCampaignIdsMap: {},
    step: STEPS[0],
    filter: this.props.filter,
    existingCreators: []
  };

  componentDidMount() {
    const {
      list,
      organizationId,
      showOnlyApproved,
      dispatchFetchListInfluencers,
      dispatchFetchApprovedListInfluencers
    } = this.props;

    if (showOnlyApproved) {
      dispatchFetchApprovedListInfluencers(organizationId, list.id)
        .then(() => {
          const ids = this.props.futureSubcampaigns.map(entry => entry.id);
          this.handleToggleCreatorsAll(ids, true);
        });
    } else {
      dispatchFetchListInfluencers(organizationId, list.id);
    }
  }

  componentWillUnmount() {
    const { dispatchReset } = this.props;

    dispatchReset();
  }

  handleToggleCampaigns = (campaignId: number): void => {
    this.setState(state => ({
      ...state,
      selectedCampaignIdsMap: {
        ...state.selectedCampaignIdsMap,
        [campaignId]: !state.selectedCampaignIdsMap[campaignId]
      },
      isModified: true
    }));
  };

  handleToggleCreators = (subcampaignId: number): void => {
    this.setState(state => ({
      ...state,
      selectedCreatorIdsMap: {
        ...state.selectedCreatorIdsMap,
        [subcampaignId]: !state.selectedCreatorIdsMap[subcampaignId]
      },
      isModified: true
    }));
  };

  handleToggleCreatorsAll = (subcampaignIds: Array<number>, isSelected: boolean): void => {
    const selectedCreatorIdsMap = {};

    subcampaignIds.forEach(id => {
      selectedCreatorIdsMap[id] = isSelected;
    });

    this.setState(state => ({
      ...state,
      selectedCreatorIdsMap,
      isModified: true
    }));
  };

  handleBack = (): void => {
    this.setState({
      step: STEPS[0]
    });
  };

  handleContinue = (): void => {
    this.setState({
      step: STEPS[1]
    });

    const { dispatchFetchCampaigns } = this.props;
    const { filter } = this.state;

    dispatchFetchCampaigns(filter);
  };

  handleDone = (): void => {
    this.setState({
      isModified: false,
      step: STEPS[2]
    });
  };

  handleSubmit = (): void => {
    const {
      futureSubcampaigns,
      campaigns,
      dispatchPerformBulkAddToShortlists,
      dispatchDanger,
      dispatchUpdateMeta
    } = this.props;
    const { selectedCampaignIdsMap, selectedCreatorIdsMap } = this.state;

    const selectedInfluencerIds = getSelectedSubcampaigns(futureSubcampaigns, selectedCreatorIdsMap).map(s => s.creatorProfile.id);
    const selectedCampaignIds = getSelectedCampaigns(campaigns, selectedCampaignIdsMap).map(c => c.id);

    dispatchPerformBulkAddToShortlists(selectedCampaignIds, selectedInfluencerIds)
      .then(this.handleExistingCreatorsMapping)
      .then(() => this.handleDone())
      .then(() => dispatchUpdateMeta(selectedInfluencerIds))
      .catch((error) => {
        const errorDetails = error?.response?.data?.detail;
        const profileId = errorDetails?.match(/\d+/)?.[0];
        const userName = futureSubcampaigns.find(entry => String(entry.id) === profileId)?.creatorProfile.name;
        if (profileId && userName) {
          dispatchDanger(errorDetails.replace(`ID ${profileId}`, `"${userName}"`));
        } else {
          dispatchDanger(errorDetails || __t('collabary_common.errors.unknown_error'));
        }
      });
  };

  handleBrandFilterChanged = (filter: string) => {
    const { dispatchFetchCampaigns } = this.props;

    dispatchFetchCampaigns(filter);

    this.setState({
      filter
    });
  };

  handleExistingCreatorsMapping = (results: Array<CreateSubcampaignsBulkResponse>) => {
    const mappedCreatorNames = results.reduce<Array<string>>((acc, item) => {
      const mappedCreators = item.existingCreators.map(creatorName => `${creatorName} – ${item.campaignName}`);
      return acc.concat(mappedCreators);
    }, []);

    this.setState({
      existingCreators: mappedCreatorNames
    });
  };

  renderStepSelectInfluencers(): React.ReactNode {
    const { areSubcampaignsLoading, futureSubcampaigns, onCloseModal } = this.props;
    const { selectedCreatorIdsMap } = this.state;

    const selectedSubcampaigns = getSelectedSubcampaigns(futureSubcampaigns, selectedCreatorIdsMap);

    return (
      <React.Fragment>
        <ModalFrameInner
          minHeight={STEP_MIN_HEIGHT}
        >
          <SubcampaignsMultiSelect
            isLoading={areSubcampaignsLoading}
            subcampaigns={futureSubcampaigns}
            selectedIdsMap={selectedCreatorIdsMap}
            titleAllWithSelectAll
            titleAll={__t('bulk_add_to_shortlist_modal.column_title_to_select_from')}
            titleSelected={__t('bulk_add_to_shortlist_modal.column_title_selected')}
            messageAllEmpty={__t('bulk_add_to_shortlist_modal.message_empty_all')}
            messageSelectedEmpty={__t('bulk_add_to_shortlist_modal.message_empty_selected')}
            onToggle={this.handleToggleCreators}
            onToggleAll={this.handleToggleCreatorsAll}
          />
        </ModalFrameInner>
        <ModalFrameFooter>
          <ActionButton
            color="tertiary"
            onClick={onCloseModal}
          >
            {__t('collabary_common.action_button.cancel')}
          </ActionButton>
          <ActionButton
            color="secondary"
            disabled={areSubcampaignsLoading || selectedSubcampaigns.length < 1}
            onClick={this.handleContinue}
          >
            {__t('collabary_common.action_button.continue')}
          </ActionButton>
        </ModalFrameFooter>
      </React.Fragment>
    );
  }

  renderStepSelectCampaigns(): React.ReactNode {
    const { campaigns, areCampaignsLoading, organizationsListIds } = this.props;
    const { selectedCampaignIdsMap } = this.state;

    const selectedCampaigns = getSelectedCampaigns(campaigns, selectedCampaignIdsMap);

    return (
      <React.Fragment>
        <ModalFrameInner
          minHeight={STEP_MIN_HEIGHT}
        >
          <div className="bulk-add-to-shortlists__filter">
            <BrandsDropdown
              hideLabel
              onChange={this.handleBrandFilterChanged}
            />
          </div>
          <CampaignsMultiSelect
            isLoading={areCampaignsLoading}
            campaigns={campaigns}
            organizationsListIds={organizationsListIds}
            selectedIdsMap={selectedCampaignIdsMap}
            onToggle={this.handleToggleCampaigns}
          />
        </ModalFrameInner>
        <ModalFrameFooter>
          <ActionButton
            color="tertiary"
            onClick={this.handleBack}
          >
            {__t('collabary_common.action_button.back')}
          </ActionButton>
          <ActionButton
            color="secondary"
            disabled={areCampaignsLoading || selectedCampaigns.length < 1}
            onClick={this.handleSubmit}
          >
            {__t('bulk_add_to_shortlist_modal.button_submit')}
          </ActionButton>
        </ModalFrameFooter>
      </React.Fragment>
    );
  }

  renderStepDone(): React.ReactNode {
    const { campaigns, list, onCloseModal } = this.props;
    const { selectedCampaignIdsMap, existingCreators } = this.state;

    const selectedCampaigns = getSelectedCampaigns(campaigns, selectedCampaignIdsMap);

    return (
      <CollectionImportedModalContent
        collectionLabel={list.label}
        minHeight={STEP_MIN_HEIGHT}
        onCloseModal={onCloseModal}
        selectedCampaigns={selectedCampaigns}
        existingCreators={existingCreators}
      />
    );
  }

  renderCurrentStep(): React.ReactNode | undefined | null {
    const { step } = this.state;

    switch (step) {
      case STEPS[0]:
        return this.renderStepSelectInfluencers();
      case STEPS[1]:
        return this.renderStepSelectCampaigns();
      case STEPS[2]:
        return this.renderStepDone();
      default:
        return null;
    }
  }

  render() {
    const { isModified, step } = this.state;

    return (
      <ModalFrame
        title={__t('bulk_add_to_shortlist_modal.title')}
        subtitle={__t(`bulk_add_to_shortlist_modal.desc_${step}`)}
      >
        {
          isModified &&
            <PromptExit type="modal" />
        }
        {this.renderCurrentStep()}
      </ModalFrame>
    );
  }
}

const mapStateToProps = ({ bulkAddToShortlists, brandsFilter, organizations }): StateProps => ({
  areSubcampaignsLoading: bulkAddToShortlists.areSubcampaignsLoading,
  areCampaignsLoading: bulkAddToShortlists.areCampaignsLoading,
  futureSubcampaigns: bulkAddToShortlists.futureSubcampaigns,
  campaigns: bulkAddToShortlists.campaigns,
  filter: brandsFilter.activeFilter,
  organizationsListIds: organizations.list.map(organization => organization.id)
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchReset: () => dispatch(reset()),
  dispatchFetchCampaigns: (filterBy) => dispatch(fetchCampaigns(filterBy)),
  dispatchFetchListInfluencers: (orgId, listId) => dispatch(fetchInfluencers(orgId, listId)),
  dispatchFetchApprovedListInfluencers: (orgId, listId) => dispatch(fetchApprovedInfluencers(orgId, listId)),
  dispatchPerformBulkAddToShortlists: (campaignIds, influencerIds) => dispatch(performBulkAddToShortlists(campaignIds, influencerIds)),
  dispatchDanger: (message) => dispatch(danger(message)),
  dispatchUpdateMeta: (influencerIds) => dispatch(fetchInfluencersMeta(influencerIds))
});

export default connect(mapStateToProps, mapDispatchToProps)(BulkAddToShortlistModal);
